import { completeCalibrationTransition } from './completeCalibrationTransition'
import type { CompoundLiftSample } from './progressionTypes'
import { supabase } from '../supabaseClient'
import type { CalibrationSessionType, CalibrationStatus } from '../calibrationTypes'
import type { CalibrationSessionMetricsInput } from './loadCalibrationProfile'
import { validateCalibrationSessionAdvance } from './validateCalibrationSessionAdvance'

export type FinalizeCalibrationSessionOptions = {
  compoundLifts?: CompoundLiftSample[]
  finalSetRir?: number
}

export type FinalizeCalibrationSessionResult = {
  error: string | null
  sessionNumber: number
  transitionTriggered: boolean
  transitionError: string | null
  alreadyRecorded?: boolean
  alreadyCompleted?: boolean
}

/**
 * Persists calibration telemetry, increments session count for new archetypes,
 * and fires the production transition when the 4th session completes.
 */
export async function finalizeCalibrationSession(
  userId: string,
  metrics: CalibrationSessionMetricsInput,
  options: FinalizeCalibrationSessionOptions = {},
): Promise<FinalizeCalibrationSessionResult> {
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('calibration_completed_sessions, calibration_status')
    .eq('id', userId)
    .single()

  if (profileError || !profile) {
    return {
      error: profileError?.message ?? 'Profile not found',
      sessionNumber: 0,
      transitionTriggered: false,
      transitionError: null,
    }
  }

  const completedSessions = Number(profile.calibration_completed_sessions ?? 0)
  const calibrationStatus = (profile.calibration_status ?? 'not_started') as CalibrationStatus

  const { data: prior, error: priorError } = await supabase
    .from('calibration_metrics')
    .select('id')
    .eq('user_id', userId)
    .eq('session_type', metrics.sessionType)
    .maybeSingle()

  if (priorError) {
    return {
      error: priorError.message,
      sessionNumber: 0,
      transitionTriggered: false,
      transitionError: null,
    }
  }

  const validation = validateCalibrationSessionAdvance(
    metrics.sessionType,
    completedSessions,
    calibrationStatus,
    Boolean(prior),
  )

  if (!validation.ok) {
    if (validation.reason === 'already_completed') {
      console.warn(`[calibration] Calibration already completed for user ${userId}. Skipping.`)
      return {
        error: null,
        sessionNumber: completedSessions,
        transitionTriggered: false,
        transitionError: null,
        alreadyCompleted: true,
      }
    }

    if (validation.reason === 'already_recorded') {
      console.warn(
        `[calibration] Session ${metrics.sessionType} already recorded for user ${userId}. Skipping.`,
      )
      return {
        error: null,
        sessionNumber: completedSessions,
        transitionTriggered: false,
        transitionError: null,
        alreadyRecorded: true,
      }
    }

    console.error(
      `[calibration] Invalid session ${metrics.sessionType} for user ${userId}. Expected session ${completedSessions + 1}.`,
    )
    return {
      error: 'Invalid calibration session sequence',
      sessionNumber: completedSessions,
      transitionTriggered: false,
      transitionError: null,
    }
  }

  const { error: upsertError } = await supabase.from('calibration_metrics').upsert(
    {
      user_id: userId,
      session_type: metrics.sessionType,
      tempo_compliance_rate: metrics.tempoComplianceRate ?? null,
      velocity_deviation_history: metrics.velocityDeviationHistory ?? [],
      work_density_dropoff: metrics.workDensityDropoff ?? null,
      heart_rate_recovery_delta: metrics.heartRateRecoveryDelta ?? {},
      cardio_pace_h_ratio: metrics.cardioPaceHrRatio ?? null,
      mobility_flags: metrics.mobilityFlags ?? [],
    },
    { onConflict: 'user_id,session_type' },
  )

  if (upsertError) {
    return {
      error: upsertError.message,
      sessionNumber: 0,
      transitionTriggered: false,
      transitionError: null,
    }
  }

  if (prior) {
    if (completedSessions >= 4) {
      const { error: transitionError } = await completeCalibrationTransition(options)
      return {
        error: null,
        sessionNumber: completedSessions,
        transitionTriggered: !transitionError,
        transitionError,
        alreadyRecorded: true,
      }
    }

    return {
      error: null,
      sessionNumber: completedSessions,
      transitionTriggered: false,
      transitionError: null,
      alreadyRecorded: true,
    }
  }

  const sessionNumber = validation.nextSessionNumber

  const { error: updateError } = await supabase
    .from('profiles')
    .update({
      calibration_completed_sessions: sessionNumber,
      calibration_status: 'in_progress',
    })
    .eq('id', userId)

  if (updateError) {
    return {
      error: updateError.message,
      sessionNumber: 0,
      transitionTriggered: false,
      transitionError: null,
    }
  }

  if (sessionNumber < 4) {
    return {
      error: null,
      sessionNumber,
      transitionTriggered: false,
      transitionError: null,
    }
  }

  const { error: transitionError } = await completeCalibrationTransition(options)

  if (transitionError) {
    await supabase
      .from('profiles')
      .update({ calibration_status: 'in_progress' })
      .eq('id', userId)

    return {
      error: null,
      sessionNumber,
      transitionTriggered: false,
      transitionError,
    }
  }

  return {
    error: null,
    sessionNumber,
    transitionTriggered: true,
    transitionError: null,
  }
}

export function metricsFromSetSession(
  sessionType: CalibrationSessionType,
  tempoComplianceRate: number,
  velocityDeviationHistory: CalibrationSessionMetricsInput['velocityDeviationHistory'],
  workDensityDropoff: number | null,
): CalibrationSessionMetricsInput {
  return {
    sessionType,
    tempoComplianceRate,
    velocityDeviationHistory,
    workDensityDropoff,
  }
}

export function defaultCompoundLiftsForSession(
  sessionType: CalibrationSessionType,
  repsCompleted: number,
  bodyWeightKg: number | null,
  workingWeightKg?: number | null,
): CompoundLiftSample[] | undefined {
  const bw = bodyWeightKg ?? 75
  if (sessionType === 'upper_compound') {
    return [
      {
        exerciseName: 'Barbell Bench Press',
        workingWeightKg: workingWeightKg ?? Math.round(bw * 0.9 * 4) / 4,
        repsCompleted,
        isCompound: true,
      },
    ]
  }
  if (sessionType === 'lower_compound') {
    return [
      {
        exerciseName: 'Barbell Back Squat',
        workingWeightKg: workingWeightKg ?? Math.round(bw * 1.2 * 4) / 4,
        repsCompleted,
        isCompound: true,
      },
    ]
  }
  return undefined
}

export function suggestedCalibrationWorkingWeight(
  sessionType: CalibrationSessionType,
  bodyWeightKg: number | null,
): number | null {
  const bw = bodyWeightKg ?? 75
  if (sessionType === 'upper_compound') return Math.round(bw * 0.9 * 4) / 4
  if (sessionType === 'lower_compound') return Math.round(bw * 1.2 * 4) / 4
  return null
}
